import { useState, useEffect } from "react";
import { apiFetch, useAuth } from "../App";

const TIER_COLORS = {
  none: "var(--text3)",
  scout: "#8fa3c8",
  analyst: "#3b82f6",
  strategist: "#8b5cf6",
  pro: "#f59e0b",
  ambassador: "#ec4899",
  elite: "#00e5b0",
  legend: "#ffd700",
};

const TIER_ICONS = {
  none: "👤",
  scout: "🔍",
  analyst: "📊",
  strategist: "♟️",
  pro: "⚡",
  ambassador: "🌟",
  elite: "💎",
  legend: "👑",
};

const MILESTONES = [
  { count: 10, bonus: 50, key: "10" },
  { count: 25, bonus: 150, key: "25" },
  { count: 50, bonus: 345, key: "50" },
  { count: 100, bonus: 790, key: "100" },
  { count: 250, bonus: 1980, key: "250" },
  { count: 500, bonus: 4455, key: "500" },
  { count: 1000, bonus: 9900, key: "1000" },
];

export default function Account({ onNavigate }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");
  const [width, setWidth] = useState(window.innerWidth);

  // Profile state
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState("");

  // Password state
  const [passwordForm, setPasswordForm] = useState({ current: "", newPass: "", confirm: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Partner state
  const [partnerStats, setPartnerStats] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Payout state
  const [payoutForm, setPayoutForm] = useState({
    payoutMethod: "ewallet",
    payoutPhone: "",
    payoutAccountName: "",
    payoutBank: "",
    payoutAccount: "",
    payoutBranch: "",
  });
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState(false);
  const [payoutError, setPayoutError] = useState("");
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutRequestSuccess, setPayoutRequestSuccess] = useState(false);
  const [payoutRequestError, setPayoutRequestError] = useState("");

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || "", phone: user.phone || "" });
    }
  }, [user]);

  useEffect(() => {
    if (tab === "partner" && !partnerStats) {
      loadPartnerStats();
    }
  }, [tab]);

  const isMobile = width < 768;

  async function loadPartnerStats() {
    setPartnerLoading(true);
    try {
      const data = await apiFetch("/partner/stats");
      setPartnerStats(data);
      setPayoutForm({
        payoutMethod: data.payoutMethod || "ewallet",
        payoutPhone: data.payoutPhone || "",
        payoutAccountName: data.payoutAccountName || "",
        payoutBank: data.payoutBank || "",
        payoutAccount: data.payoutAccount || "",
        payoutBranch: data.payoutBranch || "",
      });
    } catch (err) {
      console.error("Failed to load partner stats");
    } finally {
      setPartnerLoading(false);
    }
  }

  async function saveProfile() {
    setProfileLoading(true);
    setProfileError("");
    setProfileSuccess(false);
    try {
      await apiFetch("/partner/profile", {
        method: "PUT",
        body: JSON.stringify(profileForm),
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.message || "Failed to save profile");
    } finally {
      setProfileLoading(false);
    }
  }

  async function changePassword() {
    if (!passwordForm.current || !passwordForm.newPass) return setPasswordError("Please fill in all fields.");
    if (passwordForm.newPass !== passwordForm.confirm) return setPasswordError("Passwords do not match.");
    if (passwordForm.newPass.length < 6) return setPasswordError("Password must be at least 6 characters.");
    setPasswordLoading(true);
    setPasswordError("");
    setPasswordSuccess(false);
    try {
      await apiFetch("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword: passwordForm.current, newPassword: passwordForm.newPass }),
      });
      setPasswordSuccess(true);
      setPasswordForm({ current: "", newPass: "", confirm: "" });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err.message || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  }

    async function savePayoutDetails() {
    setPayoutLoading(true);
    setPayoutError("");
    setPayoutSuccess(false);
    try {
      const dataToSave = {
        ...payoutForm,
        payoutBank: payoutForm.payoutBank === "Other" ? payoutForm.payoutBankOther : payoutForm.payoutBank,
      };
      await apiFetch("/partner/payout-details", {
        method: "PUT",
        body: JSON.stringify(dataToSave),
      });
      setPayoutSuccess(true);
      setTimeout(() => setPayoutSuccess(false), 3000);
    } catch (err) {
      setPayoutError(err.message || "Failed to save payout details");
    } finally {
      setPayoutLoading(false);
    }
  }

  async function requestPayout() {
    setRequestingPayout(true);
    setPayoutRequestError("");
    setPayoutRequestSuccess(false);
    try {
      await apiFetch("/partner/request-payout", { method: "POST" });
      setPayoutRequestSuccess(true);
      loadPartnerStats();
    } catch (err) {
      setPayoutRequestError(err.message || "Failed to request payout");
    } finally {
      setRequestingPayout(false);
    }
  }

  function copyReferralLink() {
    if (partnerStats?.referralLink) {
      navigator.clipboard.writeText(partnerStats.referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const inputStyle = {
    width: "100%", padding: "10px 12px",
    background: "var(--bg3)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)", color: "var(--text)",
    fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
  };

  const tabs = [
    { id: "profile", label: "👤 Profile" },
    { id: "subscription", label: "💳 Subscription" },
    { id: "partner", label: "🤝 Partner Programme" },
    { id: "payout", label: "💰 Payout Settings" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{
        background: "rgba(0,229,176,0.04)", border: "1px solid rgba(0,229,176,0.15)",
        borderRadius: "var(--radius)", padding: "16px 20px",
        display: "flex", gap: 14, alignItems: "center",
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: "50%",
          background: "rgba(0,229,176,0.15)", display: "flex",
          alignItems: "center", justifyContent: "center",
          fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: "var(--accent)",
          flexShrink: 0,
        }}>
          {user?.name?.charAt(0)?.toUpperCase() || "U"}
        </div>
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>{user?.name}</div>
          <div style={{ fontSize: 13, color: "var(--text3)" }}>{user?.email}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <span className="pill pill-teal" style={{ fontSize: 10 }}>{user?.plan?.toUpperCase() || "FREE"}</span>
            {user?.partnerTier && user.partnerTier !== "none" && (
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "rgba(255,215,0,0.1)", color: TIER_COLORS[user.partnerTier] }}>
                {TIER_ICONS[user.partnerTier]} {user.partnerTier.charAt(0).toUpperCase() + user.partnerTier.slice(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, background: "var(--bg2)", borderRadius: "var(--radius-sm)", padding: 4, flexWrap: "wrap" }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1, minWidth: isMobile ? "45%" : "auto",
              padding: "8px 12px", fontSize: 12, fontWeight: 600,
              borderRadius: "var(--radius-sm)", border: "none", cursor: "pointer",
              background: tab === t.id ? "var(--accent)" : "transparent",
              color: tab === t.id ? "#000" : "var(--text3)",
              transition: "all .15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── PROFILE TAB ─────────────────────────────────────────────────── */}
      {tab === "profile" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Basic Info */}
          <div className="card" style={{ padding: "20px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
              Basic Information
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Full Name</label>
                <input
                  style={inputStyle}
                  value={profileForm.name}
                  onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Email Address</label>
                <input
                  style={{ ...inputStyle, opacity: 0.6, cursor: "not-allowed" }}
                  value={user?.email || ""}
                  disabled
                />
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Email cannot be changed. Contact support if needed.</div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Phone Number</label>
                <input
                  style={inputStyle}
                  value={profileForm.phone}
                  onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                  placeholder="+267 7X XXX XXX"
                />
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Used for payout verification only.</div>
              </div>
            </div>
            {profileError && <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 12 }}>{profileError}</div>}
            {profileSuccess && <div style={{ color: "var(--success)", fontSize: 12, marginTop: 12 }}>✅ Profile saved successfully</div>}
            <button
              className="btn btn-primary"
              onClick={saveProfile}
              disabled={profileLoading}
              style={{ marginTop: 16, width: "100%" }}
            >
              {profileLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>

          {/* Change Password */}
          <div className="card" style={{ padding: "20px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
              Change Password
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Current Password</label>
                <input
                  style={inputStyle}
                  type="password"
                  value={passwordForm.current}
                  onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>New Password</label>
                <input
                  style={inputStyle}
                  type="password"
                  value={passwordForm.newPass}
                  onChange={e => setPasswordForm({ ...passwordForm, newPass: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Confirm New Password</label>
                <input
                  style={inputStyle}
                  type="password"
                  value={passwordForm.confirm}
                  onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>
            {passwordError && <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 12 }}>{passwordError}</div>}
            {passwordSuccess && <div style={{ color: "var(--success)", fontSize: 12, marginTop: 12 }}>✅ Password changed successfully</div>}
            <button
              className="btn btn-primary"
              onClick={changePassword}
              disabled={passwordLoading}
              style={{ marginTop: 16, width: "100%" }}
            >
              {passwordLoading ? "Changing..." : "Change Password"}
            </button>
          </div>
        </div>
      )}

      {/* ─── SUBSCRIPTION TAB ────────────────────────────────────────────── */}
      {tab === "subscription" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card" style={{ padding: "20px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
              Current Plan
            </div>
            <div style={{
              padding: "20px", background: "rgba(0,229,176,0.06)",
              border: "1px solid rgba(0,229,176,0.2)", borderRadius: "var(--radius-sm)",
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22 }}>
                  {user?.plan === "free" ? "Free" : user?.plan === "edge" ? "Edge" : "Elite"}
                </div>
                <span className="pill pill-teal">{user?.plan?.toUpperCase() || "FREE"}</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--text3)" }}>
                {user?.plan === "free"
                  ? "You are on the free plan. Upgrade to unlock all features."
                  : user?.planExpiresAt
                    ? `Your plan renews on ${new Date(user.planExpiresAt).toLocaleDateString("en-BW", { day: "numeric", month: "long", year: "numeric" })}`
                    : "Active subscription"}
              </div>
            </div>

            {/* Plan comparison */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
              {[
                { name: "Free", price: "P0", features: ["Basic data", "3 platforms", "Daily updates"], current: user?.plan === "free" },
                { name: "Edge", price: "P99/mo", features: ["Everything in Free", "6 platforms", "Real-time alerts", "AI Smart Parlay", "Value bets", "Arbitrage finder"], current: user?.plan === "edge" },
                { name: "Elite", price: "P199/mo", features: ["Everything in Edge", "Poisson modeling", "Form analysis", "Portfolio tools", "Dedicated support"], current: user?.plan === "elite" },
              ].map(plan => (
                <div key={plan.name} style={{
                  padding: "14px", borderRadius: "var(--radius-sm)",
                  border: `1px solid ${plan.current ? "var(--accent)" : "var(--border)"}`,
                  background: plan.current ? "rgba(0,229,176,0.04)" : "var(--bg3)",
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{plan.name}</div>
                  <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{plan.price}</div>
                  {plan.features.map(f => (
                    <div key={f} style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>✓ {f}</div>
                  ))}
                  {plan.current && (
                    <div style={{ marginTop: 10, fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>✅ Current plan</div>
                  )}
                </div>
              ))}
            </div>

            <button
  className="btn btn-primary"
  onClick={() => onNavigate('upgrade')}
  style={{ width: "100%", padding: "14px", fontSize: 15 }}
>
  
  ⭐ Upgrade Plan →
</button>
          </div>
        </div>
      )}

      {/* ─── PARTNER PROGRAMME TAB ───────────────────────────────────────── */}
      {tab === "partner" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {partnerLoading ? (
            <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>
              Loading partner stats...
            </div>
          ) : partnerStats ? (
            <>
              {/* Tier Card */}
              <div style={{
                background: `linear-gradient(135deg, rgba(0,229,176,0.08), rgba(0,229,176,0.02))`,
                border: `1px solid ${TIER_COLORS[partnerStats.tier] || "var(--border)"}`,
                borderRadius: "var(--radius)", padding: "20px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 32, marginBottom: 4 }}>{TIER_ICONS[partnerStats.tier]}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: TIER_COLORS[partnerStats.tier] }}>
                      {partnerStats.tier === "none" ? "Not Yet Active" : partnerStats.tier.charAt(0).toUpperCase() + partnerStats.tier.slice(1)}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text3)", marginTop: 4 }}>
                      {partnerStats.activeReferrals} active referral{partnerStats.activeReferrals !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Monthly commission rate</div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, color: "var(--accent)" }}>
                      {partnerStats.currentCommissionRate ? `${(partnerStats.currentCommissionRate.l1 * 100).toFixed(0)}%` : "—"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>on direct referrals</div>
                  </div>
                </div>

                {/* Progress to next tier */}
                {partnerStats.nextTier && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>
                      <span>Progress to {partnerStats.nextTier.charAt(0).toUpperCase() + partnerStats.nextTier.slice(1)}</span>
                      <span>{partnerStats.referralsToNextTier} more referrals needed</span>
                    </div>
                    <div style={{ height: 6, background: "var(--bg3)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${Math.min(((partnerStats.activeReferrals) / (partnerStats.activeReferrals + partnerStats.referralsToNextTier)) * 100, 100)}%`,
                        background: "var(--accent)", borderRadius: 3,
                      }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Earnings Summary */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {[
                  { label: "Pending Earnings", value: `P${(partnerStats.pendingEarnings || 0).toFixed(2)}`, color: "var(--accent)", sub: "Available for payout" },
                  { label: "Total Earned", value: `P${(partnerStats.totalEarned || 0).toFixed(2)}`, color: "var(--success)", sub: "All time" },
                  { label: "Total Paid Out", value: `P${(partnerStats.totalPaidOut || 0).toFixed(2)}`, color: "var(--text2)", sub: "Received" },
                ].map(s => (
                  <div key={s.label} style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "12px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Referral Link */}
              <div className="card" style={{ padding: "20px" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  Your Referral Link
                </div>
                <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>
                  Share this link — earn commission when people subscribe
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{
                    flex: 1, padding: "10px 12px", background: "var(--bg3)",
                    borderRadius: "var(--radius-sm)", fontSize: 13, color: "var(--accent)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    border: "1px solid var(--border)",
                  }}>
                    {partnerStats.referralLink}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={copyReferralLink}
                    style={{ flexShrink: 0, padding: "10px 16px" }}
                  >
                    {copied ? "✅ Copied!" : "📋 Copy"}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>
                  Your referral code: <strong style={{ color: "var(--accent)" }}>{partnerStats.referralCode}</strong>
                </div>
              </div>

              {/* Referral Breakdown */}
              <div className="card" style={{ padding: "20px" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
                  Referral Network
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
                  {[
                    { level: "Level 1", label: "Direct referrals", data: partnerStats.breakdown?.level1, rate: partnerStats.currentCommissionRate?.l1 },
                    { level: "Level 2", label: "Their referrals", data: partnerStats.breakdown?.level2, rate: partnerStats.currentCommissionRate?.l2 },
                    { level: "Level 3", label: "3rd degree", data: partnerStats.breakdown?.level3, rate: partnerStats.currentCommissionRate?.l3 },
                  ].map(l => (
                    <div key={l.level} style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "12px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>{l.level}</div>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, color: "var(--accent)" }}>
                        {l.data?.active || 0}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text3)" }}>active / {l.data?.total || 0} total</div>
                      {l.rate > 0 && (
                        <div style={{ fontSize: 10, color: "var(--success)", marginTop: 4 }}>
                          {(l.rate * 100).toFixed(1)}% commission
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Recent referrals */}
                {partnerStats.recentReferrals?.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Recent Direct Referrals</div>
                    {partnerStats.recentReferrals.map((r, i) => (
                      <div key={i} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "8px 0", borderBottom: "1px solid var(--border)",
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text3)" }}>
                            {new Date(r.joinedAt).toLocaleDateString("en-BW")}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span className={`pill ${r.isActive ? "pill-teal" : "pill-amber"}`} style={{ fontSize: 10 }}>
                            {r.isActive ? r.plan?.toUpperCase() : "FREE"}
                          </span>
                          {r.monthlyCommission > 0 && (
                            <div style={{ fontSize: 11, color: "var(--success)", marginTop: 2 }}>
                              +P{r.monthlyCommission.toFixed(2)}/mo
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Milestones */}
              <div className="card" style={{ padding: "20px" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
                  🏆 Milestone Bonuses
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {MILESTONES.map(m => {
                    const reached = partnerStats.milestonesReached?.includes(m.key);
                    const current = partnerStats.activeReferrals >= m.count;
                    return (
                      <div key={m.key} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "10px 14px", borderRadius: "var(--radius-sm)",
                        background: reached ? "rgba(0,229,176,0.06)" : current ? "rgba(245,158,11,0.06)" : "var(--bg3)",
                        border: `1px solid ${reached ? "rgba(0,229,176,0.2)" : current ? "rgba(245,158,11,0.2)" : "var(--border)"}`,
                      }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <div style={{ fontSize: 18 }}>
                            {reached ? "✅" : current ? "⏳" : "🔒"}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{m.count} Active Referrals</div>
                            <div style={{ fontSize: 11, color: "var(--text3)" }}>
                              {reached ? "Bonus paid" : current ? "Processing next payout" : `${m.count - partnerStats.activeReferrals} more to unlock`}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: reached ? "var(--success)" : "var(--accent)" }}>
                          P{m.bonus.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Next milestone */}
              {partnerStats.nextMilestone && (
                <div style={{
                  padding: "14px 16px", background: "rgba(0,229,176,0.04)",
                  border: "1px solid rgba(0,229,176,0.15)", borderRadius: "var(--radius-sm)",
                  fontSize: 13, color: "var(--text2)",
                }}>
                  🎯 Next milestone: <strong style={{ color: "var(--accent)" }}>P{partnerStats.nextMilestone.bonus.toLocaleString()}</strong> bonus at {partnerStats.nextMilestone.count} referrals —
                  you need <strong>{partnerStats.nextMilestone.count - partnerStats.activeReferrals}</strong> more active referrals.
                </div>
              )}

              {/* Request Payout */}
              <div className="card" style={{ padding: "20px" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  Request Payout
                </div>
                <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
                  Payouts are processed on the 25th of each month. Minimum P100.
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "var(--bg3)", borderRadius: "var(--radius-sm)", marginBottom: 12 }}>
                  <span style={{ fontSize: 13, color: "var(--text3)" }}>Available to withdraw</span>
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, color: "var(--accent)" }}>
                    P{(partnerStats.pendingEarnings || 0).toFixed(2)}
                  </span>
                </div>
                {payoutRequestError && <div style={{ color: "var(--danger)", fontSize: 12, marginBottom: 8 }}>{payoutRequestError}</div>}
                {payoutRequestSuccess && <div style={{ color: "var(--success)", fontSize: 12, marginBottom: 8 }}>✅ Payout requested successfully. You will be paid on the 25th.</div>}
                <button
                  className="btn btn-primary"
                  onClick={requestPayout}
                  disabled={requestingPayout || (partnerStats.pendingEarnings || 0) < 100}
                  style={{ width: "100%" }}
                >
                  {requestingPayout ? "Requesting..." : (partnerStats.pendingEarnings || 0) < 100 ? `Need P${(100 - (partnerStats.pendingEarnings || 0)).toFixed(2)} more to withdraw` : "Request Payout"}
                </button>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8, textAlign: "center" }}>
                  Make sure your payout details are set in the Payout Settings tab before requesting.
                </div>
              </div>

              {/* Partner Programme Info */}
              <div style={{ padding: "14px 16px", background: "rgba(61,142,248,0.06)", borderRadius: "var(--radius-sm)", fontSize: 12, color: "var(--text3)", lineHeight: 1.7 }}>
                <strong>OddsEdgeBW Partner Programme:</strong> Earn commission on every active paid referral. Commission stops if a referral cancels their subscription. You must have an active paid subscription to receive cash payouts. Maximum 3 levels deep. Payouts on the 25th of each month via FNB eWallet or EFT.
              </div>
            </>
          ) : (
            <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>
              Failed to load partner stats. Please refresh.
            </div>
          )}
        </div>
      )}

      {/* ─── PAYOUT SETTINGS TAB ─────────────────────────────────────────── */}
      {tab === "payout" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card" style={{ padding: "20px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              Payout Method
            </div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
              Set up your preferred payout method for Partner Programme earnings.
            </div>

            {/* Method selector */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {[
                { key: "ewallet", label: "📱 FNB eWallet", sub: "Under P4,000" },
                { key: "eft", label: "🏦 Bank Transfer (EFT)", sub: "Any amount" },
              ].map(m => (
                <div
                  key={m.key}
                  onClick={() => setPayoutForm({ ...payoutForm, payoutMethod: m.key })}
                  style={{
                    flex: 1, padding: "12px 14px", borderRadius: "var(--radius-sm)", cursor: "pointer",
                    border: `1px solid ${payoutForm.payoutMethod === m.key ? "var(--accent)" : "var(--border)"}`,
                    background: payoutForm.payoutMethod === m.key ? "rgba(0,229,176,0.06)" : "var(--bg3)",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* eWallet fields */}
            {payoutForm.payoutMethod === "ewallet" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>FNB eWallet Phone Number</label>
                  <input
                    style={inputStyle}
                    value={payoutForm.payoutPhone}
                    onChange={e => setPayoutForm({ ...payoutForm, payoutPhone: e.target.value })}
                    placeholder="+267 7X XXX XXX"
                  />
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Must be registered FNB eWallet number. Max P4,000 per transfer.</div>
                </div>
              </div>
            )}

            {/* EFT fields */}
            {payoutForm.payoutMethod === "eft" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Account Holder Name</label>
                  <input
                    style={inputStyle}
                    value={payoutForm.payoutAccountName}
                    onChange={e => setPayoutForm({ ...payoutForm, payoutAccountName: e.target.value })}
                    placeholder="Full name as on bank account"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Bank Name</label>
                  <select
                    style={{ ...inputStyle }}
                    value={payoutForm.payoutBank}
                    onChange={e => setPayoutForm({ ...payoutForm, payoutBank: e.target.value })}
                  >
                    <option value="">Select bank</option>
                    <option value="FNB">FNB Botswana</option>
                    <option value="Standard Bank">Standard Bank Botswana</option>
                    <option value="Barclays">Absa Bank Botswana</option>
                    <option value="Stanbic">Stanbic Bank Botswana</option>
                    <option value="BancABC">BancABC Botswana</option>
                    <option value="Bank of Botswana">Bank of Botswana</option>
                    <option value="Other">Other</option>
                  </select>
                  {payoutForm.payoutBank === "Other" && (
                    <input
                      style={{ ...inputStyle, marginTop: 8 }}
                      placeholder="Enter bank name"
                      value={payoutForm.payoutBankOther || ""}
                      onChange={e => setPayoutForm({ ...payoutForm, payoutBankOther: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Account Number</label>
                  <input
                    style={inputStyle}
                    value={payoutForm.payoutAccount}
                    onChange={e => setPayoutForm({ ...payoutForm, payoutAccount: e.target.value })}
                    placeholder="Account number"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "var(--text2)", fontWeight: 600, marginBottom: 6 }}>Branch Code</label>
                  <input
                    style={inputStyle}
                    value={payoutForm.payoutBranch}
                    onChange={e => setPayoutForm({ ...payoutForm, payoutBranch: e.target.value })}
                    placeholder="e.g. 282667"
                  />
                </div>
              </div>
            )}

            {payoutError && <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 12 }}>{payoutError}</div>}
            {payoutSuccess && <div style={{ color: "var(--success)", fontSize: 12, marginTop: 12 }}>✅ Payout details saved</div>}
            <button
              className="btn btn-primary"
              onClick={savePayoutDetails}
              disabled={payoutLoading}
              style={{ marginTop: 16, width: "100%" }}
            >
              {payoutLoading ? "Saving..." : "Save Payout Details"}
            </button>
          </div>

          {/* Payout history */}
          <div className="card" style={{ padding: "20px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, marginBottom: 16 }}>
              Payout History
            </div>
            {partnerStats?.recentPayouts?.length > 0 ? (
              partnerStats.recentPayouts.map((p, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0", borderBottom: "1px solid var(--border)",
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>P{p.amount.toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>
                      {new Date(p.createdAt).toLocaleDateString("en-BW")} · {p.method === "ewallet" ? "FNB eWallet" : "EFT"}
                    </div>
                  </div>
                  <span className={`pill ${p.status === "paid" ? "pill-teal" : p.status === "pending" ? "pill-amber" : "pill-red"}`} style={{ fontSize: 10 }}>
                    {p.status.toUpperCase()}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 13, color: "var(--text3)", textAlign: "center", padding: "20px 0" }}>
                No payouts yet. Earn P100+ to request your first payout.
              </div>
            )}
          </div>

          <div style={{ padding: "14px 16px", background: "rgba(240,71,71,0.04)", border: "1px solid rgba(240,71,71,0.1)", borderRadius: "var(--radius-sm)", fontSize: 12, color: "var(--text3)", lineHeight: 1.7 }}>
            ⚠️ <strong>Important:</strong> Ensure your payout details are correct before requesting a payout. OddsEdgeBW is not responsible for payments sent to incorrect account details. Payouts are processed on the 25th of each month.
          </div>
        </div>
      )}
    </div>
  );
}